import { ChatCircleText, GearSix, SignOut, UsersThree } from '@phosphor-icons/react';
import { Avatar, Button, Icon, PinPad, Sheet, Text, buttonStyles, cn } from '@shh/ui';
import { signOut } from 'firebase/auth';
import { useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Link, useLocation } from 'react-router';
import { ChangeEmailButton, hasPassword } from '../components/ChangeEmailSheet';
import { FamilyDeleteAccountButton } from '../components/DeleteAccountSheet';
import { verifyParentPin } from '../data/api';
import type { M } from '../data/derive';
import { useFamily } from '../data/family';
import { feedbackText } from '../i18n/feedback';
import { firebase } from '../lib/firebase';
import { feedbackLink, useCanFeedback } from '../pages/FeedbackPage';
import { useLens } from './lens';
import { setLens } from './view';
import { appText } from '../i18n/app';

/**
 * "Wer schaut?" – switch the lens. Choosing is only the view, also on the wall – no PIN (Mike, 24.10.:
 * "nervig, dass … immer die PIN-Eingabe notwendig ist"). `forSettings`: "🔒 Familie" on the wall – only
 * parents, straight to the PIN if there is one; the server opens the parent mode, the view stays.
 */
export function LensSheet({
  isOpen,
  onOpenChange,
  forSettings = false,
  onUnlocked,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  forSettings?: boolean;
  onUnlocked?: () => void;
}) {
  const f = useFamily();
  const lens = useLens();
  const canFeedback = useCanFeedback();
  const { pathname } = useLocation();
  const [picked, setPicked] = useState<M | null>(null);
  const [error, setError] = useState<string>();
  const parents = f.members.filter((m) => m.role === 'parent');
  // one parent: no choice to make, the PIN pad comes right away
  const only = forSettings && parents.length === 1 ? parents[0]! : null;
  const pinFor = picked ?? only;

  const choose = (m?: M) => {
    setLens(m ? m.id : 'all');
    onOpenChange(false);
  };

  // plain function – the React compiler memoizes it (pinFor is derived from props)
  const checkPin = async (pin: string) => {
    if (!pinFor) return false;
    try {
      const r = await verifyParentPin(pin, pinFor.id);
      if (!r.ok) {
        setError(r.triesLeft ? appText.pinWrongTries(String(r.triesLeft)) : appText.pinWrong);
        return false;
      }
      setPicked(null);
      onOpenChange(false);
      onUnlocked?.();
      return true;
    } catch (e) {
      setError((e as { message?: string }).message ?? appText.pinWait);
      return false;
    }
  };

  // Kids and teens with their own login: no switching, just their own view.
  const canSwitch = lens.isWall || f.role === 'parent';

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={(o) => {
        if (!o) {
          setPicked(null);
          setError(undefined);
        }
        onOpenChange(o);
      }}
      title={
        pinFor
          ? appText.hello(pinFor.name)
          : forSettings
            ? appText.whoForSettings
            : canSwitch
              ? appText.whoWatches
              : appText.myAccount
      }
    >
      {pinFor ? (
        <div className="flex flex-col items-center gap-gap-lg">
          <PinPad onComplete={checkPin} label={appText.enterPin} error={error} />
          <Button variant="quiet" onPress={() => (only ? onOpenChange(false) : setPicked(null))}>
            {only ? appText.cancel : appText.backToChoice}
          </Button>
        </div>
      ) : forSettings ? (
        <ul className="grid grid-cols-3 gap-gap-md">
          {parents.map((m) => (
            <li key={m.id}>
              <Choice
                label={m.name}
                selected={false}
                onPress={() => setPicked(m)}
                picture={
                  <Avatar name={m.name} color={m.color} emoji={m.emoji} size="lg" decorative />
                }
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col gap-gap-xl">
          {canSwitch && (
            <ul className="grid grid-cols-3 gap-gap-md">
              <li>
                <Choice
                  label={appText.everyone}
                  selected={lens.id === 'all'}
                  onPress={() => choose()}
                  picture={
                    <span className="grid size-avatar-lg place-items-center rounded-avatar bg-accent-secondary text-accent-on-accent">
                      <Icon icon={UsersThree} size="lg" weight="fill" />
                    </span>
                  }
                />
              </li>
              {f.members.map((m) => (
                <li key={m.id}>
                  <Choice
                    label={m.name}
                    selected={lens.id === m.id}
                    onPress={() => choose(m)}
                    picture={
                      <Avatar name={m.name} color={m.color} emoji={m.emoji} size="lg" decorative />
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          {lens.isWall && (
            <Text size="sm" tone="muted">
              {appText.wallPinHint}
            </Text>
          )}
          <div className="flex flex-wrap gap-gap-sm">
            {lens.canParent && (
              <Link
                to="/einstellungen"
                onClick={() => onOpenChange(false)}
                className={buttonStyles({ variant: 'secondary' })}
              >
                <Icon icon={GearSix} size="md" /> {appText.settings}
              </Link>
            )}
            {canFeedback && (
              <Link
                {...feedbackLink(pathname)}
                onClick={() => onOpenChange(false)}
                className={buttonStyles({ variant: 'secondary' })}
              >
                <Icon icon={ChatCircleText} size="md" /> {feedbackText.entry}
              </Link>
            )}
            {!lens.isWall && (
              <Button variant="quiet" icon={SignOut} onPress={() => void signOut(firebase().auth)}>
                {appText.signOut}
              </Button>
            )}
            {/* teens have no settings – parents find it there, next to "Abmelden" */}
            {!lens.isWall && f.role === 'teen' && hasPassword() && <ChangeEmailButton />}
            {!lens.isWall && f.role === 'teen' && <FamilyDeleteAccountButton />}
          </div>
        </div>
      )}
    </Sheet>
  );
}

function Choice({
  label,
  picture,
  selected,
  onPress,
}: {
  label: string;
  picture: ReactNode;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <AriaButton
      onPress={onPress}
      aria-pressed={selected}
      className={cn(
        'flex w-full cursor-pointer flex-col items-center gap-gap-xs rounded-card p-inset-xs outline-none',
        'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover data-pressed:scale-97',
        selected && 'bg-accent-secondary-soft ring-2 ring-accent-secondary',
      )}
    >
      {picture}
      <span className="font-bold">{label}</span>
    </AriaButton>
  );
}
